import { useTheme, Typography, Button } from "@mui/material";
import Box from "@mui/material/Box";
import { StartScreenRoot } from "./StartScreen.styles";
import StyleIcon from "@mui/icons-material/Style";
import { useParallaxHoverSplit } from "../../hooks/useParallaxHover";
import { BackgroundMedia } from "../BackgroundMedia";
import wydstLogo from "../../assets/WYDST_R8S_logo.svg";
import carCrop from "../../assets/background/car_crop_2.png";

export const StartScreen = ({
  onHandleClose,
  videoInitialized,
  arcwareApplication,
  environmentLoading,
}) => {
  const theme = useTheme();

  const { containerRef, targetRef } = useParallaxHoverSplit({
    maxShift: 7.5,
    maxTilt: 1,
    spring: 0.1,
  });

  const streamingSession = localStorage.getItem("streamingSession");

  const clickOnAction = (descriptor) => {
    arcwareApplication?.emitUIInteraction(descriptor);
  };

  return (
    <StartScreenRoot ref={containerRef}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          position: "absolute",
          overflow: "hidden",
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <BackgroundMedia />
        <Box
          component="img"
          src={carCrop}
          alt="Fallback"
          sx={{
            width: "auto",
            height: { xs: "17.5rem", md: "40rem", lg: "35rem", xl: "45rem" },
            marginRight: {
              xs: "-3.5rem",
              md: "-4.5rem",
              lg: "-5rem",
              xl: "-1rem",
            },
            display: "block",
            zIndex: 1,
            willChange: "transform",
            transformStyle: "preserve-3d",
            perspective: 1000,

            [theme.breakpoints.down("lg")]: {
              marginTop: "-6rem",
              "@media (orientation: landscape)": {
                height: "20rem",
                marginTop: "-15rem",
              },
            },
          }}
          ref={targetRef}
        />
      </Box>
      <Box
        sx={{
          width: "100%",
          flexGrow: 1,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          alignItems: "center",
          padding: { xs: "2dvh 7dvw", xl: "3dvh 15dvw" },
        }}
      >
        <Box
          sx={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: { xs: "flex-start", lg: "space-between" },
            alignItems: { xs: "flex-start", lg: "flex-end" },
            [theme.breakpoints.down("lg")]: {
              "@media (orientation: landscape)": {
                flexDirection: "row",
                justifyContent: "space-between",
              },
            },
          }}
        >
          <Box
            sx={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
            }}
          >
            <Typography
              className="instructions__title"
              variant="h4"
              sx={{
                textAlign: "left",
                position: "relative",
                lineHeight: 1,
                marginBottom: -1.5,
              }}
            >
              Body kit Configurator
            </Typography>
            <Box
              component="img"
              src={wydstLogo}
              alt="Fallback"
              sx={{
                width: { xs: "100%", lg: "auto" },
                height: "auto",
                display: "block",
                zIndex: 1,
                [theme.breakpoints.down("lg")]: {
                  "@media (orientation: landscape)": {
                    width: "50rem",
                  },
                },
              }}
            />
          </Box>
          <Typography
            variant="body1"
            sx={{
              textAlign: { xs: "left", lg: "right" },
              position: "relative",
              fontSize: "1.75rem",
              maxWidth: { xs: "100%", lg: "30%" },
              marginTop: { xs: "15px", lg: "0" },
              [theme.breakpoints.down("lg")]: {
                "@media (orientation: landscape)": {
                  textAlign: "right",
                  maxWidth: "30%",
                  marginTop: "0",
                },
              },
            }}
          >
            <b>WYDST</b> takes the V10 sport car of the 4 rings brand into
            uncharted territory. A widebody, track-focused design never seen
            before, built to dominate both road and circuit.
          </Typography>
        </Box>
        <Button
          sx={{
            color: "#fff",
            background: theme?.palette?.companyColors?.accent,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "1rem",
            margin: "3rem 0",
            fontSize: { xs: "1.5rem", lg: "1.75rem" },
            padding: { xs: "1.25rem 5rem", xl: "1.25rem 12.5rem" },
            transition: "all 0.3s ease",
            pointerEvents: !videoInitialized ? "none" : "all",
            cursor: !videoInitialized ? "default" : "pointer",
            opacity: !videoInitialized ? 0.5 : 1,
            [theme.breakpoints.down("lg")]: {
              "@media (orientation: landscape)": {
                margin: "2rem 0",
              },
            },
            "& svg": {
              transition: "all 0.3s ease",
            },
          }}
          onClick={() => {
            if (!videoInitialized) return;
            const orientation =
              window.innerWidth > window.innerHeight
                ? { zoom: "0" }
                : { zoom: "-37.5" };

            clickOnAction(orientation);
            onHandleClose(false, "click");
            if (!streamingSession)
              localStorage.setItem("streamingSession", new Date());
          }}
        >
          {!videoInitialized && !environmentLoading ? (
            <>
              Loading
              <Box component="span" className="start-screen__loading__dot">
                .
              </Box>
              <Box component="span" className="start-screen__loading__dot">
                .
              </Box>
              <Box component="span" className="start-screen__loading__dot">
                .
              </Box>
            </>
          ) : (
            <>
              <Box component="span">{`${
                streamingSession ? "Resume" : "Start"
              } Configuration`}</Box>
              <StyleIcon
                sx={{
                  padding: { xs: 0, lg: "3px 0 0" },
                  fontSize: { xs: "2.75rem", lg: "3.5rem" },
                }}
              />
            </>
          )}
        </Button>
      </Box>
    </StartScreenRoot>
  );
};
